/**
 * Agent Action Service
 *
 * Frontend API client for the Agent Action Inbox system.
 * Provides access to pending actions, execution, and history.
 *
 * Date: 2026-02-18
 */

import { authFetch } from '@/utils/auth-fetch';

// Types
export interface AgentAction {
  id: string;
  agent_name: string;
  title: string;
  description: string | null;
  suggested_action_label: string | null;
  priority: 'critical' | 'high' | 'medium' | 'low';
  target_role: string;
  site_id: string | null;
  site_name?: string;
  site_domain?: string;
  action_type: string;
  action_payload: Record<string, unknown>;
  source_type: string | null;
  source_id: string | null;
  status: 'pending' | 'executed' | 'dismissed' | 'expired';
  executed_by: string | null;
  executed_by_email?: string;
  executed_at: string | null;
  execution_result: Record<string, unknown> | null;
  dismissed_by: string | null;
  dismissed_by_email?: string;
  dismissed_at: string | null;
  dismissal_reason: string | null;
  created_at: string;
  expires_at: string | null;
}

export interface ActionsResponse {
  success: boolean;
  data: AgentAction[];
  count: number;
}

export interface CountResponse {
  success: boolean;
  count: number;
}

export interface ActionResponse {
  success: boolean;
  data: AgentAction;
}

export interface ExecuteResponse {
  success: boolean;
  data: {
    success: boolean;
    action: AgentAction;
    executionResult: Record<string, unknown>;
  };
}

export interface GetActionsOptions {
  status?: 'pending' | 'executed' | 'dismissed' | 'expired';
  priority?: 'critical' | 'high' | 'medium' | 'low';
  agentName?: string;
  siteId?: string;
  limit?: number;
  offset?: number;
}

/**
 * Get pending actions for the current user
 */
export async function getPendingActions(options: GetActionsOptions = {}): Promise<ActionsResponse> {
  const params = new URLSearchParams();

  if (options.priority) params.set('priority', options.priority);
  if (options.agentName) params.set('agentName', options.agentName);
  if (options.siteId) params.set('siteId', options.siteId);
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.offset) params.set('offset', options.offset.toString());

  const queryString = params.toString();
  const url = `/agent-actions/pending${queryString ? `?${queryString}` : ''}`;

  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch pending actions');
  }
  return response.json();
}

/**
 * Get pending action count for badge display
 */
export async function getPendingCount(): Promise<number> {
  const response = await authFetch('/agent-actions/count');
  if (!response.ok) {
    throw new Error('Failed to fetch action count');
  }
  const data: CountResponse = await response.json();
  return data.count;
}

/**
 * Get action history
 */
export async function getActionHistory(options: GetActionsOptions = {}): Promise<ActionsResponse> {
  const params = new URLSearchParams();

  if (options.agentName) params.set('agentName', options.agentName);
  if (options.siteId) params.set('siteId', options.siteId);
  if (options.status) params.set('status', options.status);
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.offset) params.set('offset', options.offset.toString());

  const queryString = params.toString();
  const url = `/agent-actions/history${queryString ? `?${queryString}` : ''}`;

  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch action history');
  }
  return response.json();
}

/**
 * Get a single action by ID
 */
export async function getAction(actionId: string): Promise<ActionResponse> {
  const response = await authFetch(`/agent-actions/${actionId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch action');
  }
  return response.json();
}

/**
 * Execute an action
 */
export async function executeAction(actionId: string): Promise<ExecuteResponse> {
  const response = await authFetch(`/agent-actions/${actionId}/execute`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error('Failed to execute action');
  }
  return response.json();
}

/**
 * Dismiss an action
 */
export async function dismissAction(actionId: string, reason?: string): Promise<ActionResponse> {
  const response = await authFetch(`/agent-actions/${actionId}/dismiss`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  if (!response.ok) {
    throw new Error('Failed to dismiss action');
  }
  return response.json();
}

/**
 * Get list of unique agent names for filtering
 */
export async function getAgentNames(): Promise<string[]> {
  const response = await authFetch('/agent-actions/agents');
  if (!response.ok) {
    throw new Error('Failed to fetch agent names');
  }
  const data = await response.json();
  return data.data;
}

/**
 * Helper to get priority color class
 */
export function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'critical':
      return 'bg-red-500 hover:bg-red-600';
    case 'high':
      return 'bg-orange-500 hover:bg-orange-600';
    case 'medium':
      return 'bg-yellow-500 hover:bg-yellow-600';
    case 'low':
      return 'bg-blue-500 hover:bg-blue-600';
    default:
      return 'bg-gray-500 hover:bg-gray-600';
  }
}

/**
 * Helper to get priority badge variant
 */
export function getPriorityVariant(priority: string): 'destructive' | 'default' | 'secondary' | 'outline' {
  switch (priority) {
    case 'critical':
      return 'destructive';
    case 'high':
      return 'default';
    default:
      return 'secondary';
  }
}
